import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useState, useMemo, } from "react";
import { ToastProvider } from "./notifications/Toast";
import { translate as defaultTranslate } from "./utils/translate";
import { getPalette, getInverseTextColor } from "./utils/themeColors";
import { COLOR_DEFAULT_PALETTE_PRIMARY_100, COLOR_DEFAULT_PALETTE_PRIMARY_200, COLOR_DEFAULT_PALETTE_PRIMARY_300, COLOR_DEFAULT_PALETTE_PRIMARY_400, COLOR_DEFAULT_PALETTE_PRIMARY_500, COLOR_DEFAULT_PALETTE_PRIMARY_600, COLOR_DEFAULT_PALETTE_PRIMARY_700, COLOR_DEFAULT_PALETTE_PRIMARY_800, COLOR_DEFAULT_PALETTE_PRIMARY_900, COLOR_DEFAULT_PALETTE_SECONDARY_100, COLOR_DEFAULT_PALETTE_SECONDARY_200, COLOR_DEFAULT_PALETTE_SECONDARY_300, COLOR_DEFAULT_PALETTE_SECONDARY_400, COLOR_DEFAULT_PALETTE_SECONDARY_500, COLOR_DEFAULT_PALETTE_SECONDARY_600, COLOR_DEFAULT_PALETTE_SECONDARY_700, COLOR_DEFAULT_PALETTE_SECONDARY_800, COLOR_DEFAULT_PALETTE_SECONDARY_900, } from "@viewthespace/style-dictionary/assets/palettes/default";
const defaultPrimaryPalette = {
    100: COLOR_DEFAULT_PALETTE_PRIMARY_100,
    200: COLOR_DEFAULT_PALETTE_PRIMARY_200,
    300: COLOR_DEFAULT_PALETTE_PRIMARY_300,
    400: COLOR_DEFAULT_PALETTE_PRIMARY_400,
    500: COLOR_DEFAULT_PALETTE_PRIMARY_500,
    600: COLOR_DEFAULT_PALETTE_PRIMARY_600,
    700: COLOR_DEFAULT_PALETTE_PRIMARY_700,
    800: COLOR_DEFAULT_PALETTE_PRIMARY_800,
    900: COLOR_DEFAULT_PALETTE_PRIMARY_900,
};
const defaultSecondaryPalette = {
    100: COLOR_DEFAULT_PALETTE_SECONDARY_100,
    200: COLOR_DEFAULT_PALETTE_SECONDARY_200,
    300: COLOR_DEFAULT_PALETTE_SECONDARY_300,
    400: COLOR_DEFAULT_PALETTE_SECONDARY_400,
    500: COLOR_DEFAULT_PALETTE_SECONDARY_500,
    600: COLOR_DEFAULT_PALETTE_SECONDARY_600,
    700: COLOR_DEFAULT_PALETTE_SECONDARY_700,
    800: COLOR_DEFAULT_PALETTE_SECONDARY_800,
    900: COLOR_DEFAULT_PALETTE_SECONDARY_900,
};
export const ThemeProviderContext = createContext({
    defaultButtonShape: "square",
    translate: defaultTranslate,
    locale: "en-us",
    currencyCode: "USD",
    testAttribute: "data-test",
    linkRouteProp: "href",
    LinkComponent: "a",
    primaryPalette: defaultPrimaryPalette,
    secondaryPalette: defaultSecondaryPalette,
    primaryColor: null,
    secondaryColor: null,
    interactivePalette: defaultPrimaryPalette,
    primaryInverseTextColor: "#ffffff",
    setPrimaryPalette: () => null,
    setSecondaryPalette: () => null,
    setPrimaryColor: () => null,
    setSecondaryColor: () => null,
    restoreDefaultPalette: () => null,
    setInteractivePaletteId: () => null,
});
export const ThemeProvider = (_a) => {
    var { children, defaultButtonShape, menuZIndex, translate = defaultTranslate, locale = "en-us", currencyCode = "USD", testAttribute = "data-test", LinkComponent = "a", linkRouteProp = "href", interactivePaletteId: externalInteractivePaletteId = "primary", setCSSVariables = false } = _a, props = __rest(_a, ["children", "defaultButtonShape", "menuZIndex", "translate", "locale", "currencyCode", "testAttribute", "LinkComponent", "linkRouteProp", "interactivePaletteId", "setCSSVariables"]);
    const externalPrimaryPalette = "primaryPalette" in props && props.primaryPalette
        ? props.primaryPalette
        : null;
    const externalSecondaryPalette = "secondaryPalette" in props && props.secondaryPalette
        ? props.secondaryPalette
        : null;
    const externalPrimaryColor = "primaryColor" in props ? props.primaryColor : null;
    const externalSecondaryColor = "secondaryColor" in props ? props.secondaryColor : null;
    const [primaryPalette, setPrimaryPalette] = useState(() => {
        if (externalPrimaryPalette) {
            return externalPrimaryPalette;
        }
        if (externalPrimaryColor) {
            return getPalette(externalPrimaryColor);
        }
        return defaultPrimaryPalette;
    });
    const [secondaryPalette, setSecondaryPalette] = useState(() => {
        if (externalSecondaryPalette) {
            return externalSecondaryPalette;
        }
        if (externalSecondaryColor) {
            return getPalette(externalSecondaryColor);
        }
        return defaultSecondaryPalette;
    });
    const [primaryInverseTextColor, setPrimaryInverseTextColor] = useState(() => {
        if (externalPrimaryPalette) {
            return getInverseTextColor(externalPrimaryPalette[700]);
        }
        if (externalPrimaryColor) {
            return getInverseTextColor(externalPrimaryColor);
        }
        return "#ffffff";
    });
    const [interactivePaletteId, setInteractivePaletteId] = useState(externalInteractivePaletteId);
    const [primaryColorValue, setPrimaryColorValue] = useState(externalPrimaryColor);
    const [secondaryColorValue, setSecondaryColorValue] = useState(externalSecondaryColor);
    const setPrimaryColor = (color) => {
        setPrimaryColorValue(color);
        setPrimaryPalette(getPalette(color));
        setPrimaryInverseTextColor(getInverseTextColor(color));
    };
    const setSecondaryColor = (color) => {
        setSecondaryColorValue(color);
        setSecondaryPalette(getPalette(color));
    };
    const interactivePalette = useMemo(() => {
        if (secondaryPalette && interactivePaletteId === "secondary") {
            return secondaryPalette;
        }
        return primaryPalette;
    }, [interactivePaletteId, primaryPalette, secondaryPalette]);
    const restoreDefaultPalette = () => {
        setPrimaryPalette(defaultPrimaryPalette);
        setSecondaryPalette(defaultSecondaryPalette);
        setPrimaryInverseTextColor("#ffffff");
        setInteractivePaletteId(externalInteractivePaletteId);
    };
    useEffect(() => {
        if (externalPrimaryColor) {
            setPrimaryColor(externalPrimaryColor);
        }
        if (externalSecondaryColor) {
            setSecondaryColor(externalSecondaryColor);
        }
    }, [externalPrimaryColor, externalSecondaryColor]);
    useEffect(() => {
        if (externalPrimaryPalette) {
            setPrimaryPalette(externalPrimaryPalette);
            setPrimaryInverseTextColor(getInverseTextColor(externalPrimaryPalette[700]));
        }
        if (externalSecondaryPalette) {
            setSecondaryPalette(externalSecondaryPalette);
        }
    }, [JSON.stringify([externalPrimaryPalette, externalSecondaryPalette])]);
    useEffect(() => {
        if (!setCSSVariables)
            return;
        const styleElement = document.createElement("style");
        styleElement.type = "text/css";
        styleElement.innerHTML = `
      :root {
        --brand-primary-100: ${primaryPalette[100]};
        --brand-primary-200: ${primaryPalette[200]};
        --brand-primary-300: ${primaryPalette[300]};
        --brand-primary-400: ${primaryPalette[400]};
        --brand-primary-500: ${primaryPalette[500]};
        --brand-primary-600: ${primaryPalette[600]};
        --brand-primary-700: ${primaryPalette[700]};
        --brand-primary-800: ${primaryPalette[800]};
        --brand-primary-900: ${primaryPalette[900]};
        --brand-secondary-100: ${secondaryPalette[100]};
        --brand-secondary-200: ${secondaryPalette[200]};
        --brand-secondary-300: ${secondaryPalette[300]};
        --brand-secondary-400: ${secondaryPalette[400]};
        --brand-secondary-500: ${secondaryPalette[500]};
        --brand-secondary-600: ${secondaryPalette[600]};
        --brand-secondary-700: ${secondaryPalette[700]};
        --brand-secondary-800: ${secondaryPalette[800]};
        --brand-secondary-900: ${secondaryPalette[900]};
        --brand-primary-inverse-text: ${primaryInverseTextColor};
        --brand-interactive-100: ${interactivePalette[100]};
        --brand-interactive-200: ${interactivePalette[200]};
        --brand-interactive-300: ${interactivePalette[300]};
        --brand-interactive-400: ${interactivePalette[400]};
        --brand-interactive-500: ${interactivePalette[500]};
        --brand-interactive-600: ${interactivePalette[600]};
        --brand-interactive-700: ${interactivePalette[700]};
        --brand-interactive-800: ${interactivePalette[800]};
        --brand-interactive-900: ${interactivePalette[900]};
      }
    `;
        const head = document.getElementsByTagName("head")[0];
        head.appendChild(styleElement);
        return () => {
            styleElement.remove();
        };
    }, [
        primaryPalette,
        secondaryPalette,
        primaryInverseTextColor,
        interactivePalette,
        setCSSVariables,
    ]);
    return (_jsx(ThemeProviderContext.Provider, { value: {
            defaultButtonShape,
            menuZIndex,
            translate,
            testAttribute,
            locale,
            currencyCode,
            LinkComponent,
            linkRouteProp,
            primaryPalette,
            secondaryPalette,
            primaryColor: primaryColorValue,
            secondaryColor: secondaryColorValue,
            interactivePalette,
            primaryInverseTextColor,
            setPrimaryPalette,
            setSecondaryPalette,
            setPrimaryColor,
            setSecondaryColor,
            setInteractivePaletteId,
            restoreDefaultPalette,
        }, children: _jsx(ToastProvider, { children: children }) }));
};
//# sourceMappingURL=ThemeProvider.js.map